import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Megaphone, Play } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

interface Promo {
  id: string;
  title: string;
  caption: string | null;
  media_type: 'image' | 'video';
  media_url: string;
  thumbnail_url: string | null;
}

// One card. Videos stay paused until tapped - autoplaying several at once would
// burn a customer's bundle, which matters a lot on the mobile data most of them
// are on.
const PromoCard: React.FC<{ promo: Promo }> = ({ promo }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);

  return (
    <div className="bg-safaricab-card rounded-2xl overflow-hidden border border-white/10">
      <div className="relative bg-black/40 aspect-video">
        {promo.media_type === 'video' ? (
          <>
            <video
              ref={videoRef}
              src={promo.media_url}
              poster={promo.thumbnail_url || undefined}
              className="w-full h-full object-contain"
              controls={playing}
              playsInline
              preload="metadata"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            {!playing && (
              <button
                onClick={() => videoRef.current?.play()}
                className="absolute inset-0 flex items-center justify-center bg-black/30"
                aria-label={`Play ${promo.title}`}
              >
                <span className="w-14 h-14 rounded-full bg-safaricab-gold flex items-center justify-center shadow-2xl">
                  <Play className="w-6 h-6 text-black ml-0.5" fill="black" />
                </span>
              </button>
            )}
          </>
        ) : (
          <img src={promo.media_url} alt={promo.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-4">
        <p className="text-white font-bold text-base">{promo.title}</p>
        {promo.caption && <p className="text-white/60 text-sm mt-1">{promo.caption}</p>}
      </div>
    </div>
  );
};

// Placeholder cards for the first load. Without these the page would flash
// "No promotions right now" before the fetch lands, which reads as "there are
// none" rather than "still loading".
const PromoSkeleton: React.FC = () => (
  <div className="bg-safaricab-card rounded-2xl overflow-hidden border border-white/10">
    <div className="aspect-video bg-white/5 animate-pulse" />
    <div className="p-4 space-y-2">
      <div className="h-4 w-2/3 bg-white/5 rounded animate-pulse" />
      <div className="h-3 w-1/2 bg-white/5 rounded animate-pulse" />
    </div>
  </div>
);

// Full-screen promotions page: posters and videos published from the admin
// Promotions tab. Opened from the button under the hamburger on the home map,
// closed with the X in the top-right.
const PromotionsFeed: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [promos, setPromos] = useState<Promo[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/promotions`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!cancelled) setPromos(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) { setFailed(true); setPromos([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loading = promos === null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-safaricab-dark overflow-y-auto"
    >
      <div className="sticky top-0 z-10 bg-safaricab-dark/95 backdrop-blur-sm px-4 pt-[calc(var(--sai-top)+1rem)] pb-3 flex items-center justify-between border-b border-white/10">
        <h2 className="text-white font-bold text-xl flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-safaricab-gold" /> Promotions
        </h2>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"
          aria-label="Close promotions"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="px-4 py-4 space-y-4 pb-[calc(var(--sai-bottom)+2rem)]">
        {loading ? (
          <><PromoSkeleton /><PromoSkeleton /></>
        ) : promos.length === 0 ? (
          <div className="text-center py-24">
            <Megaphone className="w-12 h-12 text-white/15 mx-auto mb-3" />
            <p className="text-white/40 text-sm">
              {failed ? "Couldn't load promotions. Please try again." : 'No promotions right now — check back soon.'}
            </p>
          </div>
        ) : (
          promos.map(p => <PromoCard key={p.id} promo={p} />)
        )}
      </div>
    </motion.div>
  );
};

export default PromotionsFeed;
